import { useCallback } from 'react';
import { Crosshair, CircleAlert as AlertCircle, Loader as Loader2, ChevronRight } from 'lucide-react';
import { useRealtimeStatus } from '../../../lib/useRealtimeStatus';
import { useTriggers } from '../../../lib/useTriggers';
import { useApi } from '../../../lib/useApi';
import type { Account, AccountTrigger } from '../../../types';

interface Props {
  account: Account;
  onAccountUpdate: (account: Account) => void;
}

const CONFIDENCE_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: 'bg-app-positive/10', text: 'text-app-positive' },
  medium: { bg: 'bg-app-warning/10', text: 'text-app-warning' },
  low: { bg: 'bg-app-canvas', text: 'text-app-muted' },
};

const TRIGGER_TYPE_LABELS: Record<string, string> = {
  hiring_expansion: 'Hiring',
  leadership_change: 'Leadership',
  compliance_deadline: 'Compliance',
  product_launch: 'Product Launch',
  acquisition_activity: 'Acquisition',
  cost_pressure: 'Cost Pressure',
  technology_change: 'Tech Change',
  incident_event: 'Incident',
  growth_indicator: 'Growth',
  vendor_displacement: 'Vendor Shift',
};

function UrgencyBar({ score }: { score: number }) {
  const color =
    score >= 8 ? 'bg-app-critical' : score >= 5 ? 'bg-app-warning' : 'bg-app-muted';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-12 h-1.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label="Trigger urgency" aria-valuemin={0} aria-valuemax={10} aria-valuenow={score}>
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${score * 10}%` }}
        />
      </div>
      <span className="text-[10px] text-app-muted">{score}/10</span>
    </div>
  );
}

function TriggerRow({ trigger }: { trigger: AccountTrigger }) {
  const conf = CONFIDENCE_COLORS[trigger.confidence] || CONFIDENCE_COLORS.low;
  return (
    <div className="p-3.5 bg-forest-50/30 rounded-lg space-y-2 hover:bg-forest-50/60 transition-colors duration-200">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-semibold text-forest-600 bg-forest-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
              {TRIGGER_TYPE_LABELS[trigger.trigger_type] || trigger.trigger_type}
            </span>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${conf.bg} ${conf.text}`}>
              {trigger.confidence}
            </span>
          </div>
          <p className="text-sm font-medium text-app-ink leading-snug">{trigger.title}</p>
        </div>
        <UrgencyBar score={trigger.urgency_score} />
      </div>
      <p className="text-xs text-app-muted leading-relaxed line-clamp-2">{trigger.why_now}</p>
      {trigger.recommended_angle && (
        <div className="flex items-start gap-1.5">
          <ChevronRight className="w-3 h-3 text-forest-500 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-forest-700">{trigger.recommended_angle}</p>
        </div>
      )}
      {trigger.recommended_personas.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {trigger.recommended_personas.slice(0, 3).map((p, i) => (
            <span key={i} className="text-[10px] text-forest-600 bg-forest-50 px-1.5 py-0.5 rounded">
              {p}
            </span>
          ))}
        </div>
      )}
      {trigger.evidence_excerpt && (
        <p className="text-[11px] text-app-muted italic line-clamp-1">{trigger.evidence_excerpt}</p>
      )}
    </div>
  );
}

export default function TriggerRadarCard({ account, onAccountUpdate }: Props) {
  const { triggers, loading: triggersLoading, refresh: refreshTriggers } = useTriggers(account.id, account.org_id);
  const { apiGet } = useApi();
  const signalsProcessing = account.signals_status === 'processing';

  useRealtimeStatus({
    table: 'accounts',
    filterColumn: 'id',
    filterValue: account.id,
    enabled: signalsProcessing,
    statusField: 'signals_status',
    terminalStatuses: ['completed', 'error', 'none'],
    fallbackIntervalMs: 3000,
    // Poll the account's signals status through the signed AWS read instead of the
    // (stubbed) Supabase client. `GET /v1/account/{id}` returns the full account,
    // including `signals_status`, gated by `requireAccountAccess`.
    fetcher: useCallback(async () => {
      const { account: fresh } = await apiGet<{ account: Account }>(
        `/v1/account/${account.id}?org_id=${encodeURIComponent(account.org_id)}`,
      );
      return (fresh ? (fresh as unknown as Record<string, unknown>) : null);
    }, [apiGet, account.id, account.org_id]),
    onUpdate: useCallback((row: Record<string, unknown>) => {
      const data = row as unknown as Account;
      if (data.signals_status !== 'processing') {
        onAccountUpdate(data);
        if (data.signals_status === 'completed') {
          refreshTriggers();
        }
      }
    }, [onAccountUpdate, refreshTriggers]),
  });

  const hasSignals = account.signals_status === 'completed';
  const hasError = account.signals_status === 'error';
  const topTriggers = triggers.slice(0, 3);

  return (
    <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6 hover:shadow-none transition-shadow duration-300">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-app-ink flex items-center gap-2">
          <Crosshair className="w-5 h-5 text-forest-600" /> Trigger Radar
        </h2>
        {signalsProcessing && (
          <span className="inline-flex items-center gap-1.5 text-xs text-forest-600 font-medium">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Detecting...
          </span>
        )}
      </div>

      {signalsProcessing && (
        <div className="flex items-center gap-3 p-4 bg-forest-50 border border-forest-100 rounded-lg mb-4">
          <Loader2 className="w-5 h-5 text-forest-600 animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-forest-800">Analysing account signals...</p>
            <p className="text-xs text-forest-600 mt-0.5">
              Scanning news, tech stack, research, and market data for commercial triggers. Typically takes 20-40 seconds.
            </p>
          </div>
        </div>
      )}

      {hasError && account.signals_error && (
        <div className="flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg mb-4">
          <AlertCircle className="w-4 h-4 text-app-critical flex-shrink-0" />
          <p className="text-xs text-app-critical">{account.signals_error}</p>
        </div>
      )}

      {!signalsProcessing && !hasSignals && !hasError && (
        <div className="p-4 bg-app-surface border border-forest-100 rounded-lg text-center">
          <Crosshair className="w-8 h-8 text-forest-200 mx-auto mb-2" />
          <p className="text-sm text-app-muted">
            Trigger signals will appear here once account intelligence completes.
          </p>
        </div>
      )}

      {!signalsProcessing && hasSignals && !triggersLoading && topTriggers.length === 0 && (
        <div className="p-4 bg-app-surface border border-forest-100 rounded-lg text-center">
          <p className="text-sm text-app-muted">
            No active triggers detected. Insufficient evidence for commercial signals at this time.
          </p>
        </div>
      )}

      {!signalsProcessing && topTriggers.length > 0 && (
        <div className="space-y-3">
          {topTriggers.map((trigger) => (
            <TriggerRow key={trigger.id} trigger={trigger} />
          ))}
          {triggers.length > 3 && (
            <p className="text-xs text-app-muted text-center">
              +{triggers.length - 3} more signals — see Research tab for full list
            </p>
          )}
        </div>
      )}
    </div>
  );
}
